import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { FirebaseError } from 'firebase/app';
import { Subscription } from 'rxjs';
import { AuthService } from '../../core/services/auth.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  loginError = '';
  isSubmitting = false;
  private authSubscription?: Subscription;

  readonly loginForm = this.formBuilder.nonNullable.group({
    email: ['admin@srorganic.com', [Validators.required, Validators.email]],
    password: ['', [Validators.required]],
  });

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly authService: AuthService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.authSubscription = this.authService.isLoggedIn$.subscribe((isLoggedIn) => {
      if (isLoggedIn) {
        void this.router.navigateByUrl('/admin/products');
      }
    });
  }

  ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
  }

  async submit(): Promise<void> {
    this.loginError = '';

    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      this.loginError = 'Enter the owner email and password to continue.';
      return;
    }

    const { email, password } = this.loginForm.getRawValue();

    this.isSubmitting = true;

    try {
      await this.authService.login(email, password);
      await this.router.navigateByUrl('/admin/products');
    } catch (error) {
      console.error('[Admin Login Error]', error);
      this.loginError = this.getLoginErrorMessage(error);
    } finally {
      this.isSubmitting = false;
    }
  }

  private getLoginErrorMessage(error: unknown): string {
    if (error instanceof FirebaseError) {
      const messages: Record<string, string> = {
        'auth/invalid-credential': 'Invalid admin email or password.',
        'auth/user-not-found': 'No admin account exists for this email.',
        'auth/wrong-password': 'Invalid admin email or password.',
        'auth/too-many-requests': 'Too many failed attempts. Try again later or reset the password in Firebase.',
        'auth/network-request-failed': 'Network error. Check your connection and try again.',
        'auth/operation-not-allowed': 'Email/password login is not enabled in Firebase Authentication.',
        'auth/unauthorized-domain': 'This domain is not authorized in Firebase Authentication. Add it in Firebase Console or use localhost.',
      };

      return messages[error.code] || `${error.message} (${error.code})`;
    }

    return 'Unable to log in. Check the browser console for details.';
  }
}
